/****************************************************
 * Module Name:  UploadAndViewLotDocumentModal.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React, { useEffect, useState } from "react";
import { Box, Button, Typography, Tooltip } from "@mui/material";
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import Modal from "react-bootstrap/Modal";
import logo from '../../images/pansuriya.jpg'
import { LoadingButton } from "@mui/lab";
import { IoIosAddCircleOutline } from "react-icons/io";
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import Constants from "../../utility/constants";
import { IoDocumentTextOutline } from "react-icons/io5";
import { FaRegFilePdf } from "react-icons/fa";
import LotIntimationProxy from "../../utility/proxy/manager/manufacturingProxy/lotIntimationProxy";

const UploadAndViewLotDocumentModal = ({
    show,
    loader,
    handleSave,
    handleClose,
    intimationid,
}) => {
    const [selectedImages, setSelectedImages] = useState([]);
    const [uploadedDocuments, setUploadedDocuments] = useState([]);

    useEffect(() => {
        getAllDocuments();
    }, []);

    const getAllDocuments = () => {
        LotIntimationProxy.GetLotDocumentsById(intimationid, (response) => {
            if (response) {
                setUploadedDocuments(response);
            }
        })
    }

    const handleFileChange = (event) => {
        const files = event.target.files;
        if (files) {
            const filesArray = Array.from(files);
            const newImages = filesArray.map(file => ({
                url: URL.createObjectURL(file),
                name: file.name.split(".")[0],
                file: file,
            }));
            setSelectedImages((prevImages) => [...prevImages, ...newImages]);
        }
    };

    const handleRemoveImage = (index) => {
        setSelectedImages((prevImages) => prevImages.filter((_, i) => i !== index));
    };

    const handleSubmit = () => {
        let data = new FormData();

        const allNames = new Set(uploadedDocuments.map(doc => doc.docname.split(".")[0]));

        let updatedImages = selectedImages.map((image) => {
            let baseName = image.name.split(".")[0].trim();
            let extension = image.file.name.split(".").pop();
            let uniqueName = baseName;
            let counter = 1;

            while (allNames.has(uniqueName)) {
                uniqueName = `${baseName} (${counter})`;
                counter++;
            }

            allNames.add(uniqueName);
            return { ...image, name: `${uniqueName}.${extension}` };
        });

        updatedImages.forEach((image) => {
            data.append("files", image.file);
            data.append("documentnames", image.name);
        });

        handleSave(data, intimationid);
    };

    const handleRenameImage = (index, newName) => {
        // Remove leading special characters
        const sanitized = newName.replace(/^[^a-zA-Z0-9]+/, "");

        setSelectedImages((prevImages) =>
            prevImages.map((img, i) =>
                i === index ? { ...img, name: sanitized } : img
            )
        );
    };

    return (
        <div>
            <Modal
                show={show}
                onHide={handleClose}
                enforceFocus={false}
                backdrop="static"
                keyboard={true}
                size="lg"
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        <img
                            src={logo}
                            height="40pt"
                            width="40pt"
                            alt={Constants.COMPANY_SITE}
                        />
                        &nbsp;&nbsp;Upload And View Documents
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body style={{ height: '500px', overflowY: 'auto', paddingBottom: '50px' }}>
                    <Box sx={{
                        paddingTop: "10px",
                        "& .MuiOutlinedInput-input": {
                            padding: "9px !important"
                        },
                        "& .MuiInputLabel-root": {
                            top: "-7px !important"
                        }
                    }}>
                        <div style={{ position: "relative", padding: "20px" }}>
                            <Button
                                style={{
                                    position: "absolute",
                                    top: "0px",
                                    right: "19px",
                                    padding: "10px",
                                    color: "white",
                                    border: "none",
                                    cursor: "pointer",
                                    zIndex: 1 // Ensure button is always on top
                                }}
                                component="label"
                                variant="contained"
                                size="small"
                                startIcon={<IoIosAddCircleOutline size={20} />}
                                onClick={() => document.getElementById("fileInput").click()}
                            >
                                ADD
                            </Button>

                            <input
                                id="fileInput"
                                type="file"
                                multiple
                                accept="image/*, .pdf"
                                style={{ display: "none" }}
                                onChange={handleFileChange}
                            />
                            {selectedImages.length !== 0 ? (
                                <Box sx={{ display: "flex", width: "47%", alignItems: "center", marginLeft: "2px" }}>
                                    <Box sx={{ display: "flex", alignItems: "center", width: "100%", paddingRight: "15px" }}>
                                        <Typography sx={{ fontWeight: "bold" }}> Selected Documents:</Typography>
                                    </Box>
                                </Box>) : null}

                            {selectedImages.length !== 0 ? (
                                <div style={{ marginTop: "25px", padding: "10px", border: "1px dashed gray", minHeight: "200px" }}>
                                    <div style={{ display: "flex", flexWrap: "wrap", gap: "25px", marginTop: "10px", marginLeft: "45px" }}>
                                        {selectedImages.map((image, index) => {
                                            const isImage = ["png", "jpg", "jpeg", "gif", "bmp", "webp", "jfif"].includes(image.file.name.split(".")[1]);
                                            const isPDF = image.file.name.split(".")[1] === "pdf";
                                            return (
                                                <div key={index} style={{ position: "relative" }}>
                                                    {isImage ? (
                                                        <img
                                                            src={image.url}
                                                            alt={`selected-img-${index}`}
                                                            style={{
                                                                width: "100px",
                                                                height: "100px",
                                                                objectFit: "cover",
                                                                border: "2px solid #ddd",
                                                                borderRadius: "8px",
                                                                boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
                                                            }}
                                                        />
                                                    ) : (
                                                        <div
                                                            style={{
                                                                width: "100px",
                                                                height: "100px",
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                border: "2px solid #ddd",
                                                                borderRadius: "8px",
                                                                boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
                                                                backgroundColor: "#f5f5f5",
                                                            }}
                                                        >
                                                            {isPDF ? (
                                                                <FaRegFilePdf size={80} color="#940000" />
                                                            ) : (
                                                                <IoDocumentTextOutline size={80} />
                                                            )}
                                                        </div>
                                                    )}
                                                    <HighlightOffIcon
                                                        style={{
                                                            position: "absolute",
                                                            top: "-7px",
                                                            right: "-10px",
                                                            backgroundColor: "white",
                                                            borderRadius: "60%",
                                                            color: "black",
                                                            cursor: "pointer",
                                                            fontSize: "22px",
                                                        }}
                                                        onClick={() => handleRemoveImage(index)}
                                                    />
                                                    <div>
                                                        <input
                                                            type="text"
                                                            value={image.name}
                                                            onChange={(e) => handleRenameImage(index, e.target.value)}
                                                            placeholder="Enter file name"
                                                            style={{
                                                                marginTop: "5px",
                                                                width: "100px",
                                                                padding: "5px",
                                                                fontSize: "12px",
                                                                textAlign: "center",
                                                                border: `1px solid ${image.name ? '#ddd' : 'red'}`,
                                                                borderRadius: "4px",
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>) : null}

                            {/* Display Already Uploaded Documents */}
                            <Box sx={{ display: "flex", width: "47%", alignItems: "center", marginLeft: "2px", marginTop: "20px" }}>
                                <Box sx={{ display: "flex", alignItems: "center", width: "100%", paddingRight: "15px" }}>
                                    <Typography sx={{ textAlign: "center", marginTop: "2px", fontWeight: "bold" }}>
                                        Uploaded Documents:
                                    </Typography>
                                </Box>
                            </Box>
                            <div style={{ marginTop: "25px" }}>
                                {uploadedDocuments.length === 0 ? (
                                    <Typography sx={{ textAlign: "center", color: "gray", marginTop: "135px" }}>
                                        No documents uploaded.
                                    </Typography>
                                ) : (
                                    <div style={{ display: "flex", flexWrap: "wrap", gap: "23px" }}>
                                        {uploadedDocuments.map((doc, index) => {
                                            const isImage = ["png", "jpg", "jpeg", "gif", "bmp", "webp", "jfif"].includes(doc.docname.split(".")[1]);
                                            const isPDF = doc.docname.split(".")[1] === "pdf";
                                            return (
                                                <div key={index} style={{ position: "relative" }}>
                                                    <a href={doc.document} target="_blank" rel="noopener noreferrer"
                                                        onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.05)")}
                                                        onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
                                                        style={{
                                                            display: "inline-block",
                                                            transition: "transform 0.1s ease",
                                                        }}
                                                    >
                                                        {isImage ? (
                                                            <img
                                                                src={doc.document}
                                                                alt={`uploaded-doc-${index}`}
                                                                style={{
                                                                    width: "100px",
                                                                    height: "100px",
                                                                    objectFit: "cover",
                                                                    border: "2px solid #ddd",
                                                                    borderRadius: "8px",
                                                                    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
                                                                }}
                                                            />
                                                        ) : (
                                                            <div
                                                                style={{
                                                                    width: "100px",
                                                                    height: "100px",
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    justifyContent: "center",
                                                                    border: "2px solid #ddd",
                                                                    borderRadius: "8px",
                                                                    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
                                                                    backgroundColor: "#f5f5f5",
                                                                }}
                                                            >
                                                                {isPDF ? (
                                                                    <FaRegFilePdf size={80} color="#940000" />
                                                                ) : (
                                                                    <IoDocumentTextOutline size={80} />
                                                                )}
                                                            </div>
                                                        )}
                                                    </a>
                                                    <Tooltip title={doc.docname.split(".")[0]} arrow>
                                                        <Typography
                                                            sx={{
                                                                marginTop: "5px",
                                                                fontSize: "15px",
                                                                textAlign: "center",
                                                                whiteSpace: "nowrap",
                                                                overflow: "hidden",
                                                                textOverflow: "ellipsis",
                                                                width: "100px",
                                                                display: "block",
                                                            }}
                                                        >
                                                            {doc.docname.split(".")[0]}
                                                        </Typography>
                                                    </Tooltip>
                                                </div>)
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                    </Box>
                </Modal.Body>

                <Modal.Footer>
                    <Box sx={{
                        "& .Mui-disabled": {
                            backgroundColor: "rgba(0, 0, 0, 0.12) !important",
                            color: "rgba(0, 0, 0, 0.26) !important"
                        }
                    }}>
                        <LoadingButton
                            sx={{ mr: 1 }}
                            variant="contained"
                            color="info"
                            onClick={handleSubmit}
                            loading={loader}
                            loadingPosition="start"
                            startIcon={<AssignmentTurnedInIcon />}
                            type="Submit"
                            disabled={selectedImages.length === 0 || selectedImages.some(img => !img.name)}
                        >
                            Submit
                        </LoadingButton>
                    </Box>
                    <Button variant="contained" color="error" onClick={handleClose}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    )
};
export default UploadAndViewLotDocumentModal;